import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import ModalController from '../modal_controller'

export default class extends ModalController {
  static values = {
    threatCategoryId: String,
    threatCategoryName: String,
    threatCategoryChildren: Array
  }

  static targets = [
    'downArrow', 'rightArrow', 'error',
    'addCriticalButton', 'deleteCriticalButton', 'addSevereButton', 'deleteSevereButton', 'addModerateButton', 'deleteModerateButton',
    'modalTitle', 'modalText'
  ]

  showHideChildren () {
    this.downArrowTarget.classList.toggle('d-none')
    this.rightArrowTarget.classList.toggle('d-none')
    this.threatCategoryChildrenValue.forEach(child => {
      const childElement = document.getElementById(child)
      childElement.classList.toggle('d-none')
    })
  }

  addCriticalThreatCategory () {
    fetchWithHandlingLoginRedirect(
      '/internal/api/threat-categories',
      {
        method: 'POST',
        body: JSON.stringify({
          name: this.threatCategoryNameValue + ' - critical',
          parentId: this.threatCategoryIdValue,
          threatLevel: 'critical',
          priority: 3
        })
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json()
      })
      .then(data => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.renderedThreatCategory
        const newThreatCategory = tempDiv.firstChild

        this.element.insertAdjacentElement('afterend', newThreatCategory)

        const children = this.threatCategoryChildrenValue
        children.splice(0, 0, newThreatCategory.id)
        this.threatCategoryChildrenValue = children

        this.addCriticalButtonTarget.classList.add('d-none')
        this.deleteCriticalButtonTarget.dataset.threatCategoryId = newThreatCategory.id
        this.deleteCriticalButtonTarget.classList.remove('d-none')
      })
      .catch(() => {
        this.displayError()
      })
  }

  addSevereThreatCategory () {
    fetchWithHandlingLoginRedirect(
      '/internal/api/threat-categories',
      {
        method: 'POST',
        body: JSON.stringify({
          name: this.threatCategoryNameValue + ' - severe',
          parentId: this.threatCategoryIdValue,
          threatLevel: 'severe',
          priority: 2
        })
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json()
      })
      .then(data => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.renderedThreatCategory
        const newThreatCategory = tempDiv.firstChild

        const children = this.threatCategoryChildrenValue
        let inserted = false
        for (let i = this.threatCategoryChildrenValue.length - 1; i >= 0; i--) {
          const child = this.threatCategoryChildrenValue[i]
          const childElement = document.getElementById(child)
          if (childElement.dataset.threatLevel === 'critical') {
            childElement.insertAdjacentElement('afterend', newThreatCategory)
            children.splice(1, 0, newThreatCategory.id)
            inserted = true
            break
          }
        }

        if (!inserted) {
          this.element.insertAdjacentElement('afterend', newThreatCategory)
          children.splice(0, 0, newThreatCategory.id)
        }
        this.threatCategoryChildrenValue = children

        this.addSevereButtonTarget.classList.add('d-none')
        this.deleteSevereButtonTarget.dataset.threatCategoryId = newThreatCategory.id
        this.deleteSevereButtonTarget.classList.remove('d-none')
      })
      .catch(() => {
        this.displayError()
      })
  }

  addModerateThreatCategory () {
    fetchWithHandlingLoginRedirect(
      '/internal/api/threat-categories',
      {
        method: 'POST',
        body: JSON.stringify({
          name: this.threatCategoryNameValue + ' - moderate',
          parentId: this.threatCategoryIdValue,
          threatLevel: 'moderate',
          priority: 1
        })
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json()
      })
      .then(data => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.renderedThreatCategory
        const newThreatCategory = tempDiv.firstChild

        const children = this.threatCategoryChildrenValue
        let inserted = false
        for (let i = this.threatCategoryChildrenValue.length - 1; i >= 0; i--) {
          const child = this.threatCategoryChildrenValue[i]
          const childElement = document.getElementById(child)
          if (childElement.dataset.threatLevel === 'severe') {
            childElement.insertAdjacentElement('afterend', newThreatCategory)
            children.splice(2, 0, newThreatCategory.id)
            inserted = true
            break
          } else if (childElement.dataset.threatLevel === 'critical') {
            childElement.insertAdjacentElement('afterend', newThreatCategory)
            children.splice(1, 0, newThreatCategory.id)
            inserted = true
            break
          }
        }

        if (!inserted) {
          this.element.insertAdjacentElement('afterend', newThreatCategory)
          children.splice(0, 0, newThreatCategory.id)
        }
        this.threatCategoryChildrenValue = children

        this.addModerateButtonTarget.classList.add('d-none')
        this.deleteModerateButtonTarget.dataset.threatCategoryId = newThreatCategory.id
        this.deleteModerateButtonTarget.classList.remove('d-none')
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  openModal (event) {
    switch (event.currentTarget.dataset.threatLevel) {
      case 'critical': {
        this.modalTitleTarget.innerHTML = 'Delete Critical Threat Category?'
        this.modalTextTarget.innerHTML = `This action will remove all keywords from the ${this.threatCategoryNameValue} Critical threat category and you will no longer be alerted on these keywords. Are you sure you want to delete this threat category?`
        break
      }
      case 'severe': {
        this.modalTitleTarget.innerHTML = 'Delete Severe Threat Category?'
        this.modalTextTarget.innerHTML = `This action will remove all keywords from the ${this.threatCategoryNameValue} Severe threat category and you will no longer be alerted on these keywords. Are you sure you want to delete this threat category?`
        break
      }
      case 'moderate': {
        this.modalTitleTarget.innerHTML = 'Delete Moderate Threat Category?'
        this.modalTextTarget.innerHTML = `This action will remove all keywords from the ${this.threatCategoryNameValue} Moderate threat category and you will no longer be alerted on these keywords. Are you sure you want to delete this threat category?`
        break
      }
      case 'parent': {
        this.modalTitleTarget.innerHTML = `Delete ${this.threatCategoryNameValue} Threat Category?`
        this.modalTextTarget.innerHTML = 'This action will remove this category and all sub-levels and you will no longer be alerted on any of these keywords. Are you sure you want to delete this threat category?'
        break
      }
    }

    this.modalTarget.dataset.threatCategoryId = event.currentTarget.dataset.threatCategoryId
    this.modalTarget.dataset.threatLevel = event.currentTarget.dataset.threatLevel

    super.openModal()
  }

  confirmDeleteThreatCategory (event) {
    switch (this.modalTarget.dataset.threatLevel) {
      case 'critical': {
        this.deleteCriticalThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
      case 'severe': {
        this.deleteSevereThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
      case 'moderate': {
        this.deleteModerateThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
      case 'parent': {
        this.deleteParentThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
    }

    super.closeModal()
  }

  deleteParentThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        this.element.remove()
        this.threatCategoryChildrenValue.forEach(child => {
          const childElement = document.getElementById(child)
          childElement.remove()
        })
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  deleteCriticalThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        const childElement = document.getElementById(threatCategoryId)
        childElement.remove()
        this.threatCategoryChildrenValue = this.threatCategoryChildrenValue.filter(child => child !== threatCategoryId)

        this.addCriticalButtonTarget.classList.remove('d-none')
        this.deleteCriticalButtonTarget.dataset.threatCategoryId = null
        this.deleteCriticalButtonTarget.classList.add('d-none')
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  deleteSevereThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        const childElement = document.getElementById(threatCategoryId)
        childElement.remove()
        this.threatCategoryChildrenValue = this.threatCategoryChildrenValue.filter(child => child !== threatCategoryId)

        this.addSevereButtonTarget.classList.remove('d-none')
        this.deleteSevereButtonTarget.dataset.threatCategoryId = null
        this.deleteSevereButtonTarget.classList.add('d-none')
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  deleteModerateThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        const childElement = document.getElementById(threatCategoryId)
        childElement.remove()
        this.threatCategoryChildrenValue = this.threatCategoryChildrenValue.filter(child => child !== threatCategoryId)

        this.addModerateButtonTarget.classList.remove('d-none')
        this.deleteModerateButtonTarget.dataset.threatCategoryId = null
        this.deleteModerateButtonTarget.classList.add('d-none')
      })
      .catch(() => {
        this.displayError()
      })
  }

  displayError () {
    this.errorTarget.innerHTML = 'Error: Please contact Raven support'
    this.errorTarget.classList.remove('d-none')
    setTimeout(() => {
      this.errorTarget.classList.add('d-none')
    }, 2000)
  }
}
